import { gql, useMutation, useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useMount, useTitle } from "react-use";
import Button from "../../../components/Button";
import CurrencyInput from "../../../components/forms/CurrencyInput";
import { FormField } from "../../../components/forms/shared";
import TextInput from "../../../components/forms/TextInput";
import Select from "../../../components/Select";
import Spinner from "../../../components/Spinner";

const Financial = () => {
  useTitle("Settings - Financial Background | Due");
  const [employmentValue, setEmploymentValue] =
    useState<{
      label: string;
      value: string;
    } | null>();

  const [annualIncomeValue, setAnnualIncomeValue] = useState<string>();

  const { data, loading, error, refetch } = useQuery(gql`
    {
      onboarding {
        employment
        occupation
        employer
        annualIncome
      }
    }
  `);

  const [updateOnboarding] = useMutation(gql`
    mutation UpdateOnboarding($input: OnboardingMutationInput!) {
      onboarding(input: $input) {
        employment
      }
    }
  `);

  const { employment, occupation, employer, annualIncome } =
    data && data.onboarding
      ? data.onboarding
      : {
          employment: "",
          occupation: "",
          employer: "",
          annualIncome: "",
        };

  useMount(() => {
    document.getElementById("app-container")?.scrollTo({ top: 0 });
    refetch();
  });

  useEffect(() => {
    if (!loading && data && employment) {
      setEmploymentValue(
        EMPLOYMENT_OPTIONS.find((option) => option.value === employment || null)
      );
    }
    if (!loading && data && annualIncome) {
      setAnnualIncomeValue(annualIncome);
    }
  }, [data, employment, annualIncome, loading]);

  let content;
  if (loading) {
    content = (
      <div className="w-full h-80 flex items-center justify-center">
        <Spinner />
      </div>
    );
  } else if (error) {
    content = (
      <div className="h-80 flex items-center justify-center">
        <div className="text-red-500">
          Failed to load user data. Please try again later.
        </div>
      </div>
    );
  } else {
    content = (
      <Formik
        validateOnChange={false}
        validateOnBlur={true}
        initialValues={{
          employment,
          occupation,
          employer,
          annualIncome,
        }}
        enableReinitialize
        onSubmit={({ employment, occupation, employer, annualIncome }) => {
          updateOnboarding({
            variables: {
              input: {
                employment,
                occupation,
                employer,
                annualIncome,
              },
            },
          })
            .then(() => {
              refetch();
              toast("Financial information saved.");
            })
            .catch(() => {
              toast(
                "Financial information failed to save. Please try again later."
              );
            });
        }}
      >
        {({ values, errors, setFieldValue }) => (
          <Form>
            <FormField label="Employment">
              <Select
                placeholder="Select employment..."
                value={employmentValue}
                options={EMPLOYMENT_OPTIONS}
                onChange={(value) => {
                  if (value) {
                    setFieldValue("employment", value.value);
                    setEmploymentValue(value);
                  }
                }}
              />
            </FormField>
            <FormField
              id="occupation"
              label="Occupation"
              error={errors.occupation}
            >
              <TextInput
                id="occupation"
                name="occupation"
                value={values.occupation}
                onChange={(e) => {
                  setFieldValue("occupation", e.target.value);
                }}
              />
            </FormField>
            <FormField id="employer" label="Employer" error={errors.employer}>
              <TextInput
                id="employer"
                name="employer"
                value={values.employer}
                onChange={(e) => {
                  setFieldValue("employer", e.target.value);
                }}
              />
            </FormField>
            <FormField
              id="annualIncome"
              label="Annual Income"
              error={errors.annualIncome}
            >
              <CurrencyInput
                id="annualIncome"
                name="annualIncome"
                value={annualIncomeValue}
                prefix="$"
                allowNegativeValue={false}
                onValueChange={(value) => {
                  const parsedNumber = Number(value);
                  if (!isNaN(parsedNumber)) {
                    setFieldValue("annualIncome", parsedNumber);
                    setAnnualIncomeValue(value);
                  } else {
                    setFieldValue("annualIncome", 0);
                    setAnnualIncomeValue("");
                  }
                }}
              />
            </FormField>
            <Button type="submit">Save</Button>
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <div className="p-6 mb-16">
      <h2 className="text-lg leading-6 font-medium text-gray-900">
        Financial Background
      </h2>
      <p className="mt-1 text-sm text-gray-500 mb-4">
        Fill out financial background to help us better understand your
        financial situation.
      </p>
      {content}
    </div>
  );
};

const EMPLOYMENT_OPTIONS = [
  { label: "Employed (Full Time)", value: "full-time" },
  { label: "Employed (Part Time)", value: "part-time" },
  { label: "Unemployed", value: "unemployed" },
  { label: "Retired", value: "retired" },
];

export default Financial;
